import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import Lenis from "lenis";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  Heart,
  Target,
  Sparkles,
  Zap,
  Award,
  Users,
  Coffee,
  ArrowRight,
  Quote,
  TrendingUp,
  ShieldCheck,
  Globe2,
  Rocket
} from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import Process from "../components/Process";
import VisionaryPathGame from "../components/VisionaryPathGame";
import SEO from "../components/SEO";
import SectionStepper from "../components/SectionStepper";

const frameworkSteps = [
  {
    step: "01",
    title: "People",
    desc: "Our core asset and the heartbeat of every successful brand. We believe in empowering talent to drive innovation.",
    image: "/images/about/people.png"
  },
  {
    step: "02",
    title: "Process",
    desc: "Meticulous, scalable systems that turn complex visions into reality with technical precision and creative flair.",
    image: "/images/about/process.png"
  },
  {
    step: "03",
    title: "Partnership",
    desc: "A skin-in-the-game approach that binds our success to yours, fostering long-term collaborative growth.",
    image: "/images/about/partnership.png"
  },
  {
    step: "04",
    title: "Promotion",
    desc: "Cinematic storytelling and high-impact digital campaigns that capture attention and build lasting legacies.",
    image: "/images/about/promotion.png"
  },
  {
    step: "05",
    title: "Performance",
    desc: "Data-driven results and sustainable economic impact. We measure our success by the visible change we create.",
    image: "/images/about/performance.png"
  }
];

const About = () => {
  useEffect(() => {
    window.scrollTo(0, 0);

    const lenis = new Lenis({
      duration: 1.15,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
      wheelMultiplier: 1,
      touchMultiplier: 1.4
    });

    let raf;
    const tick = (time) => {
      lenis.raf(time);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);

    window.lenis = lenis;
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
      window.lenis = null;
    };
  }, []);

  return (
    <div className="bg-[var(--ink)] text-[var(--cream)] min-h-screen">
      <SEO
        title="About Our Studio"
        description="Learn about Meraki Solutions, a full-service agency and MBDC capital partner specializing in creative design, digital marketing, and brand strategy."
      />
      <div className="grain" />
      <Navbar />

      <main>
        <SectionStepper 
          sections={[
            { id: "top", label: "Vision" },
            { id: "purpose", label: "Our Purpose" },
            { id: "philosophy", label: "Philosophy" },
            { id: "team", label: "Team" },
            { id: "framework", label: "Framework" },
            { id: "cta", label: "Join Us" }
          ]} 
        />
        {/* Hero Section - The MBDC Concept */}
        <section id="top" className="relative min-h-screen flex items-center pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-gradient-to-b from-[var(--ink)] via-transparent to-[var(--ink)] z-10" />
          <motion.div
            initial={{ scale: 1.1, opacity: 0 }}
            animate={{ scale: 1, opacity: 0.35 }}
            transition={{ duration: 2, ease: "easeOut" }}
            className="w-full h-full"
          >
            <video
              autoPlay
              muted
              loop
              playsInline
              preload="auto"
              className="w-full h-full object-cover brightness-90"
            >
              <source src="https://videos.pexels.com/video-files/3205627/3205627-hd_1920_1080_25fps.mp4" type="video/mp4" />
            </video>
          </motion.div>
        </div>

        <div className="container-xl w-full relative z-20 px-6">
          <motion.div
            initial={{ y: 50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="max-w-5xl"
          >
            <span className="font-impact text-[var(--gold)] tracking-[0.4em] uppercase text-sm mb-6 block">
              ESTABLISHED 2014
            </span>
            <h1 className="font-display text-5xl md:text-7xl lg:text-8xl mb-10 leading-[1] tracking-tight">
              Your 360° <span className="italic font-serif-accent gold-text">Partner</span> <br />
              for Branding, Media & Growth.
            </h1>
            <p className="max-w-3xl text-[var(--cream-muted)] text-xl md:text-2xl font-body leading-relaxed mb-12">
              We are pioneers of a skin-in-the-game model, providing Marketing, Media, Branding, Digital, Creative, Communication & Consulting solutions.
            </p>
            <div className="flex flex-wrap gap-6">
              <a href="#purpose" className="btn-primary">
                Explore Our Vision <ArrowRight className="w-5 h-5 ml-2" />
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* The Core Story Section - Vision & Mission */}
      <section id="purpose" className="py-32 relative">
        <div className="container-xl px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-start">
            <div className="lg:col-span-5">
              <Kicker index="01" label="Our Purpose" />
              <h2 className="font-display text-4xl md:text-5xl lg:text-6xl mb-8 leading-tight">
                Vision & <span className="italic gold-text">Mission</span>
              </h2>
              <div className="space-y-6 text-lg text-[var(--muted)] font-body leading-relaxed">
                <p>
                  <span className="text-[var(--cream)] font-medium">We expertise in Creative Designing, AV Designing, Web and Mobile development, Social Media Management, Viral & Search Marketing, Digital Marketing Strategy, Concept & Brand Designing, SEM, SEO, and Ad network services.</span>
                </p>
                <p>
                  We not only seek to be superb in execution, but believe in adding value by providing consultancy & innovative creative ideas in the digital environment of a brand.
                </p>
                <p>
                  We are based out of Bhopal and working with many major brands across India and globally, including Mumbai, Delhi, Bangalore, Chennai, US and UK.
                </p>
              </div>
            </div>

            <div className="lg:col-span-7 grid grid-cols-1 md:grid-cols-2 gap-8">
              {[
                {
                  icon: <ShieldCheck className="w-10 h-10" />,
                  title: "Skin In The Game",
                  desc: "We don't just advise; we co-partner. Our success is directly tied to the growth and sustainability of the brands we serve.",
                  image: "/images/about/skin_in_the_game.png"
                },
                {
                  icon: <TrendingUp className="w-10 h-10" />,
                  title: "Value Unlocked",
                  desc: "We target to create value & solve complex branding & business problems through the lens of MBDC benchmarks.",
                  image: "/images/about/value_unlocked.png"
                },
                {
                  icon: <Globe2 className="w-10 h-10" />,
                  title: "Global Footprint",
                  desc: "We are privileged to work with the most exciting Startups, SMEs, and Founder-run companies in India & abroad.",
                  image: "/images/about/global_footprint.png"
                },
                {
                  icon: <Rocket className="w-10 h-10" />,
                  title: "Startup Attitude",
                  desc: "We are & will be a startup working with scalable companies. The best companies remain startups as long as they can.",
                  image: "/images/about/startup_attitude.png"
                }
              ].map((card, idx) => (
                <motion.div
                  key={idx}
                  whileHover={{ y: -10 }}
                  className={`relative p-8 rounded-2xl overflow-hidden bg-[var(--ink-2)] border border-[var(--rule)] hover:border-[var(--gold)] transition-all duration-500 group h-full flex flex-col justify-end min-h-[280px] ${idx >= 2 ? "md:mt-8" : ""}`}
                >
                  {/* Background Image with Hover Effect */}
                  <div
                    className="absolute inset-0 z-0 transition-all duration-700 grayscale group-hover:grayscale-0 opacity-10 group-hover:opacity-30 scale-110 group-hover:scale-100"
                    style={{
                      backgroundImage: `url(${card.image})`,
                      backgroundSize: "cover",
                      backgroundPosition: "center"
                    }}
                  />

                  {/* Overlay for legibility */}
                  <div className="absolute inset-0 z-10 bg-gradient-to-t from-[var(--ink)] via-[var(--ink)]/40 to-transparent" />

                  <div className="relative z-20">
                    <div className="text-[var(--gold)] mb-6 transform group-hover:scale-110 transition-transform duration-500 origin-left">
                      {card.icon}
                    </div>
                    <h3 className="font-display text-2xl mb-4 text-[var(--cream)]">{card.title}</h3>
                    <p className="text-[var(--muted)] text-sm leading-relaxed group-hover:text-[var(--cream-muted)] transition-colors duration-500">
                      {card.desc}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Philosophy Section - MBDC Pioneers with Scroll Reveal */}
      <section id="philosophy" className="bg-[var(--ink)] relative">
        <PhilosophyReveal />
        <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-[var(--rule)] to-transparent" />
      </section>

      <VisionaryPathGame />

      {/* Team & Culture Section */}
      <section id="team" className="py-32 relative">
        <div className="container-xl px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-24 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="aspect-[4/3] rounded-2xl overflow-hidden border border-[var(--rule)] shadow-2xl relative">
                <video
                  autoPlay
                  muted
                  loop
                  playsInline
                  preload="auto"
                  className="w-full h-full object-cover grayscale hover:grayscale-0 transition-all duration-1000"
                >
                  <source src="/videos/about-video.mp4" type="video/mp4" />
                </video>
              </div>
              <div className="absolute -bottom-8 -left-8 p-8 bg-[var(--gold)] text-[var(--ink)] rounded-2xl hidden md:block">
                <div className="font-impact text-4xl">NO CLIENTS.</div>
                <div className="font-display text-xl italic">Only Partners.</div>
              </div>
            </motion.div>

            <div>
              <Kicker index="03" label="Team & Culture" />
              <h2 className="font-display text-4xl md:text-5xl mb-8">Dynamic. Authentic. <br />Efficient.</h2>
              <div className="space-y-6 text-[var(--muted)] font-body leading-relaxed text-lg">
                <p>
                  We do not function under traditional company structures. We don't have employees; we have <span className="text-[var(--gold)] font-medium">Partners</span>. Some work inside Meraki, some outside.
                </p>
                <p>
                  We are practically brand promoters and business cheerleaders. We inspire & facilitate value through effective campaigns and a can-do attitude, speed of decision-making, and self-belief.
                </p>
                <p>
                  Our vision is that we bring value for all our partners & hope to have a positive impact on all their journeys.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values & Impact Section - The 5P Framework */}
      <Process
        id="framework"
        steps={frameworkSteps}
        title="The 5P Framework"
        kicker={{ index: "04", label: "Our Philosophy" }}
      />

      {/* Final CTA */}
      <section id="cta" className="py-32 relative overflow-hidden">
        <div className="container-xl px-6 text-left relative z-10">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="max-w-4xl"
          >
            <h2 className="font-display text-5xl md:text-7xl mb-10">
              Serve the Market. <br />
              <span className="italic gold-text">Support the Country.</span>
            </h2>
            <p className="text-[var(--cream-muted)] text-xl mb-12 font-body max-w-2xl">
              We look forward to serving the markets, community, society & our country’s economy in collaboration with the coveted brands we connect with.
            </p>
            <div className="flex flex-col md:flex-row items-start justify-start gap-6">
              <Link to="/contact" className="btn-primary">
                Join the Journey <ArrowRight className="w-5 h-5 ml-2" />
              </Link>
              <Link to="/services" className="btn-ghost">
                Our Services
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      </main>
      <Footer />
    </div>
  );
};

const PhilosophyReveal = () => {
  const container = useRef(null);
  const { scrollYProgress } = useScroll({
    target: container,
    offset: ["start start", "end end"]
  });

  const text = '"MBDC Capital Partners" is a term we made up, since no word exists for what we do. We live for creating impact, value & visible change. We will remove ourselves from any assignment where we cannot achieve this.';
  const words = text.split(" ");

  return (
    <div ref={container} className="relative h-[600vh] bg-[var(--ink)]">
      <div className="sticky top-0 h-screen flex items-center overflow-hidden">
        <div className="container-xl px-4 md:px-6 w-full">
          <div className="max-w-6xl mx-auto">
            <div className="mb-12 md:mb-20">
              <Kicker index="02" label="Our Philosophy" />
              <h2 className="mt-6 font-display text-3xl md:text-6xl leading-tight">
                Pioneering <span className="italic gold-text">Visible Change.</span>
              </h2>
            </div>
            
            <div className="relative">
              <div className="absolute -top-12 -left-12 opacity-5 hidden lg:block">
                <Quote size={180} className="text-[var(--gold)]" />
              </div>
              
              <div className="flex flex-wrap text-[clamp(20px,4.5vw,56px)] font-display italic leading-[1.25] text-[var(--cream)]">
                {words.map((word, i) => {
                  const start = (i / words.length) * 0.75;
                  const end = start + (1 / words.length) * 0.75;
                  return (
                    <Word key={i} progress={scrollYProgress} range={[start, end]}>
                      {word}
                    </Word>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
        
        {/* Progress bar for the reveal */}
        <div className="absolute bottom-0 left-0 w-full h-1 bg-white/5">
          <motion.div 
            className="h-full bg-[var(--gold)]"
            style={{ scaleX: scrollYProgress, transformOrigin: "0%" }}
          />
        </div>
      </div>
    </div>
  );
};

const Word = ({ children, progress, range }) => {
  const opacity = useTransform(progress, range, [0.12, 1]);
  return (
    <span className="relative inline-block mr-[0.35em] mb-[0.2em]">
      <span className="absolute top-0 left-0 opacity-[0.04] select-none pointer-events-none whitespace-nowrap">
        {children}
      </span>
      <motion.span style={{ opacity }} className="relative z-10 whitespace-nowrap">
        {children}
      </motion.span>
    </span>
  );
};

const Kicker = ({ index, label }) => (
  <div className="flex items-center gap-4 mb-4">
    <span className="font-impact text-[11px] tracking-[0.4em] text-[var(--gold)]">{index}</span>
    <span className="w-16 h-px bg-[var(--rule)]" />
    <span className="font-impact text-[11px] tracking-[0.4em] text-[var(--cream-muted)] uppercase">{label}</span>
  </div>
);

export default About;
